// Globals
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch } from '@/store';
import { getSettings, updateSettings } from '../../feature';

// Components
import { showSuccessToast, useForm } from '@/components';
import { CheckmarkIcon, EditIcon } from '@/icons';
import { Button } from '@/ui';
import { SettingsDetails, SettingsForm, StyledPageTitle } from '../../components';
import { StyledWrapper } from './SettingsContainerStyles';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { useBoolean } from '@/hooks';
import { UpdateSettingsDTO } from '../../dtos';
import { getInitialValues } from '../../helpers/geyInitialValues';
import { TSettingsFormValues } from '../../helpers/types';
import { useGetSettings } from '../../hooks';

const SettingsContainer = () => {
  const { t } = useTranslation(['settings']);
  const { canManage } = usePermissions();
  const canManageSettings = canManage(Resource.SETTING);
  const { value: isEdit, setTrue, setFalse } = useBoolean();
  const { groupedSettings } = useGetSettings();
  const dispatch = useAppDispatch();
  const [formInstance] = useForm();

  const handleSubmit = async (values: TSettingsFormValues) => {
    const dto = new UpdateSettingsDTO(values);

    try {
      await dispatch(updateSettings(dto)).unwrap();
      await dispatch(getSettings());
      setFalse();
      showSuccessToast({ message: t('data_saved') });
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <StyledWrapper>
        <StyledPageTitle>{t('page_title')}</StyledPageTitle>
        {canManageSettings && isEdit && (
          <Button icon={<CheckmarkIcon />} onClick={formInstance.submit}>
            {t('save_changes')}
          </Button>
        )}
        {canManageSettings && !isEdit && (
          <Button icon={<EditIcon />} onClick={setTrue}>
            {t('button_edit')}
          </Button>
        )}
      </StyledWrapper>
      {canManageSettings && isEdit ? (
        <SettingsForm form={formInstance} initialValues={getInitialValues(groupedSettings)} onSubmit={handleSubmit} />
      ) : (
        <SettingsDetails settings={groupedSettings} />
      )}
    </>
  );
};

export default SettingsContainer;
